import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useActiveTicket } from "../hooks/useActiveTicket";
import { useActiveTickets } from "../hooks/useActiveTickets";
import { Button } from "./ui/button";
import { LogOut, Ticket, Wrench, ShieldCheck, History, Building2, Settings as SettingsIcon, CreditCard, User as UserIcon, ArrowLeft, Home } from "lucide-react";
import BypassEstablishmentIcon from "./BypassEstablishmentIcon";
import BypassTicketIcon from "./BypassTicketIcon";
import ActiveTicketsPills from "./ActiveTicketsPills";
import { formatTicketCode } from "../lib/ticketCode";

export default function Header() {
  const { role, user, commerce, visitorAccountsEnabled, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = role === "client" && user?.isAdmin;
  const activeTicket = useActiveTicket();
  const activeTickets = useActiveTickets();
  // Visitor-only journey (home, search, my-ticket, /commerce/<id>, etc.):
  // no Header right side when visitor accounts are disabled. The
  // "Connexion / S'inscrire" shortcut is reserved for the authentication
  // pages of the establishment journey (login + register).
  const path = location.pathname || "/";
  const onEstablishmentAuthRoute =
    path === "/commerce/login" ||
    path === "/commerce/register" ||
    path.startsWith("/merchant/");
  // Strict commerce auth routes (login/register only) — these MUST always
  // expose the "Connexion / S'inscrire" shortcuts, even when the visitor is
  // already logged in as a guest with active tickets. /merchant/* is the
  // signed-in dashboard area and is intentionally excluded.
  const onCommerceAuthRoute =
    path === "/commerce/login" || path === "/commerce/register";

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border bg-white/70 backdrop-blur-xl">
      <div className="container mx-auto flex h-16 items-center justify-between px-3 md:px-8 gap-2">
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <Link to="/" className="flex flex-col items-start shrink-0 leading-none" data-testid="logo-link">
            <img src="/logo-bypassgo-b-only.png" alt="BypassGo" translate="no" style={{ height: 38, width: "auto" }} className="object-contain sm:h-11" />
            <span className="font-display font-black text-[10px] sm:text-xs tracking-tight mt-0.5 leading-none" translate="no">
              <span style={{ color: "#1D2D7B" }}>Bypass</span><span style={{ color: "#2BB54A" }}>Go</span>
            </span>
          </Link>
          {role !== "commerce" && !onEstablishmentAuthRoute && (
            <>
              {path !== "/" && (
                <Link
                  to={activeTicket ? "/my-ticket" : "/"}
                  className="flex items-center justify-center rounded-md hover:bg-brand/5 transition-colors p-1"
                  aria-label={activeTicket ? "Voir mon ticket en cours" : "BypassGo — Ticket visiteur"}
                  data-testid="header-visitor-ticket-icon"
                >
                  <BypassTicketIcon size={32} />
                </Link>
              )}
              {path !== "/" && (
                <Link
                  to="/"
                  className="flex items-center justify-center rounded-md text-ink hover:bg-brand/5 hover:text-brand transition-colors w-9 h-9 border border-border"
                  aria-label="Retour à l'accueil"
                  title="Accueil"
                  data-testid="header-home-btn"
                >
                  <Home size={18} strokeWidth={2.2} />
                </Link>
              )}
            </>
          )}
        </div>
        <nav className="flex items-center gap-1 sm:gap-2 md:gap-3 overflow-x-auto no-scrollbar">
          {!role && visitorAccountsEnabled && (
            <>
              <Button asChild variant="ghost" size="sm" className="font-medium" data-testid="nav-login-btn">
                <Link to="/login">Connexion</Link>
              </Button>
              <Button asChild size="sm" className="bg-brand hover:bg-brand-hover text-white rounded-md font-medium" data-testid="nav-signup-btn">
                <Link to="/register">S'inscrire</Link>
              </Button>
            </>
          )}
          {!role && !visitorAccountsEnabled && onEstablishmentAuthRoute && (
            /* Visitor accounts disabled — expose "Connexion" / "S'inscrire"
               ONLY on the establishment journey so merchants can still reach
               their dashboard. The visitor journey keeps an empty top-right. */
            <>
              <Button asChild variant="ghost" size="sm" className="font-medium" data-testid="nav-commerce-login-btn">
                <Link to="/commerce/login">Connexion</Link>
              </Button>
              <Button asChild size="sm" className="bg-brand hover:bg-brand-hover text-white rounded-md font-medium" data-testid="nav-commerce-signup-btn">
                <Link to="/commerce/register">S'inscrire</Link>
              </Button>
            </>
          )}
          {role === "client" && user?.isGuest && (
            /* Guest visitor (anonymous ticket) — no profile, no logout,
               no admin shortcuts. Show every active ticket as a pill so the
               visitor can jump straight to any of their (up to 3) tickets.
               On commerce auth routes we ALSO surface "Connexion / S'inscrire"
               so a merchant who happens to also be a guest visitor can still
               reach their establishment account. */
            <>
              <ActiveTicketsPills tickets={activeTickets} />
              {onCommerceAuthRoute && (
                <>
                  <Button asChild variant="ghost" size="sm" className="font-medium" data-testid="nav-commerce-login-btn">
                    <Link to="/commerce/login">Connexion</Link>
                  </Button>
                  <Button asChild size="sm" className="bg-brand hover:bg-brand-hover text-white rounded-md font-medium" data-testid="nav-commerce-signup-btn">
                    <Link to="/commerce/register">S'inscrire</Link>
                  </Button>
                </>
              )}
              {!onCommerceAuthRoute && path !== "/" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
                  data-testid="nav-back-btn"
                  aria-label="Retour"
                  className="flex items-center gap-1.5"
                >
                  <ArrowLeft size={16} /> <span className="hidden sm:inline">Retour</span>
                </Button>
              )}
            </>
          )}
          {role === "client" && !user?.isGuest && (
            <>
              <ActiveTicketsPills tickets={activeTickets} />
              <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex" data-testid="nav-my-ticket-btn">
                <Link to="/my-ticket" className="flex items-center gap-2"><Ticket size={16} /> Mon ticket</Link>
              </Button>
              <Button asChild variant="ghost" size="sm" className="hidden md:inline-flex" data-testid="nav-history-btn">
                <Link to="/my-tickets" className="flex items-center gap-2"><History size={16} /> Historique</Link>
              </Button>
              <Button asChild variant="ghost" size="sm" data-testid="nav-support-btn">
                <Link to="/my-requests" className="flex items-center gap-2"><Wrench size={16} /> <span className="hidden sm:inline">Support</span></Link>
              </Button>
              <Button asChild variant="ghost" size="sm" className="hidden lg:inline-flex" data-testid="nav-cards-btn">
                <Link to="/my-cards" className="flex items-center gap-2"><CreditCard size={16} /> <span className="hidden sm:inline">Cartes</span></Link>
              </Button>
              <Button asChild variant="ghost" size="sm" className="hidden lg:inline-flex" data-testid="nav-settings-btn">
                <Link to="/settings" className="flex items-center gap-2"><SettingsIcon size={16} /> <span className="hidden sm:inline">Paramètres</span></Link>
              </Button>
              {isAdmin && (
                <Button asChild variant="ghost" size="sm" data-testid="nav-admin-btn">
                  <Link to="/admin" className="flex items-center gap-2 text-brand"><ShieldCheck size={16} /> <span className="hidden sm:inline">Admin</span></Link>
                </Button>
              )}
              <span className="hidden md:inline text-sm text-subtle">{user?.prenom}</span>
              <Button asChild variant="outline" size="sm" className="border border-ink rounded-md" data-testid="nav-profile-btn">
                <Link to="/profile" className="flex items-center gap-2"><UserIcon size={14} /> <span className="hidden sm:inline">Mon profil</span></Link>
              </Button>
              <Button variant="outline" size="sm" onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout-btn">
                <LogOut size={16} />
              </Button>
            </>
          )}
          {role === "commerce" && (
            <>
              {path !== "/" && (
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  className="flex items-center justify-center w-9 h-9 p-0 border border-border hover:bg-brand/5 hover:text-brand"
                  data-testid="nav-merchant-home-btn"
                  aria-label="Accueil"
                  title="Accueil"
                >
                  <Link to="/"><Home size={18} strokeWidth={2.2} /></Link>
                </Button>
              )}
              {/* Dashboard shortcut — hidden for unverified merchants since the
                  profile icon already brings them back to /check-email (same
                  effective destination). Showing both would be redundant. */}
              {commerce?.emailVerified !== false && (
                <Button asChild variant="ghost" size="sm" data-testid="nav-dashboard-btn">
                  <Link to="/merchant/dashboard" className="flex items-center gap-2"><BypassEstablishmentIcon size={18} /> <span className="hidden sm:inline">Tableau de bord</span></Link>
                </Button>
              )}
              <Button asChild variant="ghost" size="sm" data-testid="nav-support-btn">
                <Link to="/my-requests" className="flex items-center gap-2"><Wrench size={16} /> <span className="hidden sm:inline">Support</span></Link>
              </Button>
              <span className="hidden md:inline text-sm text-subtle">{commerce?.name}</span>
              {/* Unverified merchant : profile icon brings them BACK to the
                  /check-email verification page (instead of /merchant/profile)
                  so they don't lose their way to confirm the email. The
                  logout button is hidden in that state — they cancel via
                  the "Annuler la création" CTA on /check-email itself. */}
              {commerce?.emailVerified === false ? (
                <Button asChild variant="outline" size="sm" className="border border-ink rounded-md" data-testid="nav-merchant-verify-btn">
                  <Link to="/check-email" className="flex items-center gap-2" title="Revenir à la vérification de votre email">
                    <UserIcon size={14} /> <span className="hidden sm:inline">Vérification</span>
                  </Link>
                </Button>
              ) : (
                <>
                  <Button asChild variant="outline" size="sm" className="border border-ink rounded-md" data-testid="nav-merchant-profile-btn">
                    <Link to="/merchant/profile" className="flex items-center gap-2"><UserIcon size={14} /> <span className="hidden sm:inline">Mon profil</span></Link>
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout-btn">
                    <LogOut size={16} />
                  </Button>
                </>
              )}
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
